import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { IUser } from '@/models/user.model';
import { IAuthReducerState } from '@/store/reducers/auth.reducer';
import { startImpersonation } from '@/store/actions/auth.action';
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button.tsx";
import { UserCheck, Search, Coins, Loader2, AlertCircle, FolderKanban, Crown } from "lucide-react";
import "./UserListScreen.scss";
import TableWrapperComponent from '@/components/common-components/table-wrapper/TableWrapperComponent';
import { ITableColumn } from '@/models/table.model';
import APIConfig from '@/constants/APIConfig';
import { Input } from '@/components/ui/input';
import commonService from '@/services/common.service.ts';
import AddCreditDialogComponent from './AddCreditDialogComponent';
import { useIsMobile } from '@/hooks/use-mobile';
import { IAPIResponseType } from '@/models/api.model';
import CommonService from '@/services/common.service.ts';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ADMIN_USER_LIST_PATH } from '@/constants/RoutesConfig';

const UserListScreen: React.FC = () => {
    const [activeTab, setActiveTab] = useState<string>('users');
    const [impersonationError, setImpersonationError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');
    const [projectsSearchQuery, setProjectsSearchQuery] = useState<string>('');
    const [debouncedProjectsSearchQuery, setDebouncedProjectsSearchQuery] = useState<string>('');
    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [isAddCreditDialogOpen, setIsAddCreditDialogOpen] = useState<boolean>(false);

    // Mobile-specific state for users
    const [users, setUsers] = useState<IUser[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [totalUsers, setTotalUsers] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(20);

    // Projects state
    const [projects, setProjects] = useState<any[]>([]);
    const [isProjectsLoading, setIsProjectsLoading] = useState<boolean>(false);
    const [isProjectsError, setIsProjectsError] = useState<boolean>(false);
    const [projectsCurrentPage, setProjectsCurrentPage] = useState<number>(1);
    const [projectsTotalPages, setProjectsTotalPages] = useState<number>(1);
    const [totalProjects, setTotalProjects] = useState<number>(0);
    const [projectsPageSize, setProjectsPageSize] = useState<number>(20);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const isMobile = useIsMobile();

    const isCurrentUserAdmin = useSelector((state: { auth: IAuthReducerState }) => state.auth.isAdmin);
    const currentUser = useSelector((state: { auth: IAuthReducerState }) => state.auth.user);

    // Extract project ID from URL when navigating from /admin/project/:technology/:projectId
    useEffect(() => {
        const pathMatch = location.pathname.match(/^\/admin\/project\/[^/]+\/([^/]+)$/);
        const projectIdFromParams = params.projectId;

        if (pathMatch && pathMatch[1]) {
            const projectId = pathMatch[1];
            // Set the project ID in the search query
            setProjectsSearchQuery(projectId);
            // Switch to projects tab
            setActiveTab('projects');
            // Reset to first page
            setProjectsCurrentPage(1);
            // Navigate to the admin users page to clean up URL
            if (location.pathname !== ADMIN_USER_LIST_PATH) {
                navigate(ADMIN_USER_LIST_PATH, { replace: true });
            }
        } else if (projectIdFromParams) {
            // Fallback: use params if available
            setProjectsSearchQuery(projectIdFromParams);
            setActiveTab('projects');
            setProjectsCurrentPage(1);
            if (location.pathname !== ADMIN_USER_LIST_PATH) {
                navigate(ADMIN_USER_LIST_PATH, { replace: true });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, params.projectId]);

    // Debounce the search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 1000); // 1000ms delay

        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Debounce the projects search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedProjectsSearchQuery(projectsSearchQuery);
        }, 1000); // 1000ms delay

        return () => clearTimeout(timer);
    }, [projectsSearchQuery]);

    // Memoized search payload to prevent unnecessary re-renders
    const searchPayload = useMemo(() => ({
        page: currentPage,
        page_size: pageSize,
        email: debouncedSearchQuery.trim() || undefined,
        exclude_email: currentUser?.email // Exclude current user from the list
    }), [debouncedSearchQuery, currentUser?.email, currentPage, pageSize]);

    // Projects search payload
    const projectsSearchPayload = useMemo(() => {
        const searchTerm = debouncedProjectsSearchQuery.trim();
        const payload: any = {
            page: projectsCurrentPage,
            page_size: projectsPageSize,
        };

        // Add search parameters if search term exists
        if (searchTerm) {
            // Try to match by ID, name, or user_email
            payload.id = searchTerm;
        }

        return payload;
    }, [projectsCurrentPage, projectsPageSize, debouncedProjectsSearchQuery]);

    // API call function for mobile
    const fetchUsers = useCallback(async () => {
        if (!isMobile) return; // Only fetch for mobile view

        setIsLoading(true);
        setIsError(false);

        try {
            const response: IAPIResponseType<any> = await commonService._api.post(
                APIConfig.GET_ALL_USERS.URL,
                searchPayload
            );

            if (response?.data) {
                const userList = response.data.users || response.data || [];
                const pagination = response.data.pagination || {};

                setUsers(userList);
                setTotalUsers(pagination.total || 0);
                setTotalPages(Math.ceil((pagination.total || 0) / pageSize));
            } else {
                setUsers([]);
                setIsError(true);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            setUsers([]);
            setIsError(true);
        } finally {
            setIsLoading(false);
        }
    }, [isMobile, searchPayload, pageSize]);

    // Fetch users when mobile view and search changes
    useEffect(() => {
        if (isMobile && activeTab === 'users') {
            fetchUsers();
        }
    }, [fetchUsers, isMobile, activeTab]);

    // API call function for projects (mobile)
    const fetchProjects = useCallback(async () => {
        if (!isMobile) return; // Only fetch for mobile view

        setIsProjectsLoading(true);
        setIsProjectsError(false);

        try {
            const response: IAPIResponseType<any> = await commonService._api.post(
                APIConfig.GET_ALL_PROJECTS.URL,
                projectsSearchPayload
            );


            if (response?.data) {
                const projectsList = response.data.projects || [];
                const pagination = response.data.pagination || {};
                console.log('Projects list:', projectsList);
                setProjects(projectsList);
                setTotalProjects(pagination.total || 0);
                setProjectsTotalPages(Math.ceil((pagination.total || 0) / projectsPageSize));
            } else {
                setProjects([]);
                setIsProjectsError(true);
            }
        } catch (error) {
            console.error('Error fetching projects:', error);
            setProjects([]);
            setIsProjectsError(true);
        } finally {
            setIsProjectsLoading(false);
        }
    }, [isMobile, projectsSearchPayload, projectsPageSize]);

    // Fetch projects when mobile view changes
    useEffect(() => {
        if (isMobile && activeTab === 'projects') {
            fetchProjects();
        }
    }, [fetchProjects, isMobile, activeTab]);

    const handleImpersonate = async (userData: any) => {
        setImpersonationError(null);
        commonService._account.getUserTokenByIdAPICall({ email: userData?.email })
            .then((response: any) => {
                if (response?.data && response?.data?.user && response?.data?.token) {
                    const { user: targetUser, token: targetUserToken } = response.data;

                    dispatch(startImpersonation(targetUser, targetUserToken));
                    navigate('/');
                }
            })
            .catch((error: any) => {
                CommonService._alert.showToast(error?.response?.data?.message || 'Failed to impersonate user', 'error');
            });
    };

    const handleImpersonateProject = async (project: any) => {
        setImpersonationError(null);

        if (!project.user_id) {
            CommonService._alert.showToast('Project does not have a user id associated', 'error');
            return;
        }

        commonService._account.getUserTokenByIdAPICall({ id: project.user_id })
            .then((response: any) => {
                if (response?.data && response?.data?.user && response?.data?.token) {
                    const { user: targetUser, token: targetUserToken } = response.data;

                    dispatch(startImpersonation(targetUser, targetUserToken));
                    // Navigate directly to the project page
                    setTimeout(() => {
                        const appType = (project?.app_type || "nextjs").toLowerCase();
                        navigate(`/project/${appType}/${project.id || project._id}`);
                    }, 10);
                }
            })
            .catch((error: any) => {
                CommonService._alert.showToast(error?.response?.data?.message || 'Failed to impersonate user for project', 'error');
            });
    };

    const handleAddCredit = (user: any) => {
        setSelectedUser(user);
        setIsAddCreditDialogOpen(true);
    };

    const handleCloseAddCreditDialog = () => {
        setIsAddCreditDialogOpen(false);
        setSelectedUser(null);
    };

    const handleMakePro = async (user: IUser) => {
        const userId = user._id || (user as any).id;
        if (!userId) {
            CommonService._alert.showToast('User ID is missing', 'error');
            return;
        }

        try {
            const response = await commonService._account.addAdminSubscription({
                user_id: userId
            });
            if (response?.msg) {
                CommonService._alert.showToast(response?.msg || 'User upgraded to Pro successfully', 'success');
            }
        } catch (error: any) {
            console.error('Error making user pro:', error);
            CommonService._alert.showToast(error?.response?.data?.message || 'Failed to make user Pro', 'error');
        }
    };

    // Use useCallback to prevent unnecessary re-renders
    const handleSearchChange = useCallback((value: string) => {
        setSearchQuery(value);
        setCurrentPage(1); // Reset to first page when searching
    }, []);

    // Projects search handler
    const handleProjectsSearchChange = useCallback((value: string) => {
        setProjectsSearchQuery(value);
        setProjectsCurrentPage(1); // Reset to first page when searching
    }, []);

    // Pagination handlers for mobile
    const handlePageChange = useCallback((page: number) => {
        setCurrentPage(page);
    }, []);

    const handlePageSizeChange = useCallback((newPageSize: number) => {
        setPageSize(newPageSize);
        setCurrentPage(1); // Reset to first page when changing page size
    }, []);

    // Pagination handlers for projects (mobile)
    const handleProjectsPageChange = useCallback((page: number) => {
        setProjectsCurrentPage(page);
    }, []);

    const handleProjectsPageSizeChange = useCallback((newPageSize: number) => {
        setProjectsPageSize(newPageSize);
        setProjectsCurrentPage(1); // Reset to first page when changing page size
    }, []);

    const columns: ITableColumn[] = [
        {
            key: "id",
            dataIndex: "_id",
            title: "ID",
            width: 50,
        },
        {
            key: "name",
            dataIndex: "name",
            title: "Name",
            width: 50,
        },
        {
            key: "email",
            dataIndex: "email",
            title: "Email",
            width: 50,
        },
        {
            key: "actions",
            title: "Actions",
            width: 450,
            render: (row: IUser) => (
                <div className="flex items-center gap-2">
                    <Button
                        onClick={() => handleImpersonate(row)}
                        className="flex items-center gap-2 border border-gray-300 bg-white hover:bg-gray-50 text-sm h-8 px-3"

                    >
                        <UserCheck className="h-4 w-4" />
                        Impersonate
                    </Button>
                    <Button
                        onClick={() => handleAddCredit(row)}
                        className="flex items-center gap-2 border border-gray-300 bg-white hover:bg-gray-50 text-sm h-8 px-3"
                    >
                        <Coins className="h-4 w-4" />
                        Add Credit
                    </Button>
                    <Button
                        onClick={() => handleMakePro(row)}
                        className="flex items-center gap-2 border border-gray-300 bg-white hover:bg-gray-50 text-sm h-8 px-3 text-amber-600 hover:text-amber-700"
                    >
                        <Crown className="h-4 w-4" />
                        Make Pro
                    </Button>
                </div>
            ),
        },
    ];

    // Projects columns for desktop table
    const projectsColumns: ITableColumn[] = [
        {
            key: "id",
            dataIndex: "_id",
            title: "Project ID",
            width: 100,
        },
        {
            key: "name",
            dataIndex: "name",
            title: "Project Name",
            width: 150,
        },
        {
            key: "app_type",
            dataIndex: "app_type",
            title: "App Type",
            width: 100,
            render: (row: any) => (
                <span className="uppercase">{row.app_type || 'N/A'}</span>
            ),
        },
        {
            key: "created_at",
            dataIndex: "created_at",
            title: "Created At",
            width: 150,
            render: (row: any) => (
                <span>{row.created_at ? commonService.transformTimeStamp(row.created_at) : 'N/A'}</span>
            ),
        },
        {
            key: "actions",
            title: "Actions",
            width: 200,
            render: (row: any) => (
                <div className="flex items-center gap-2">
                    <Button
                        onClick={() => handleImpersonateProject(row)}
                        className="flex items-center gap-2 border border-gray-300 bg-white hover:bg-gray-50 text-sm h-8 px-3"
                    >
                        <UserCheck className="h-4 w-4" />
                        Impersonate
                    </Button>
                </div>
            ),
        },
    ];

    useEffect(() => {
        if (!isCurrentUserAdmin) {
            const originalAdminStateExists = !!localStorage.getItem('originalAdminState');
            if (originalAdminStateExists) {
                navigate('/');
            }
        }
    }, [isCurrentUserAdmin, navigate]);

    if (!isCurrentUserAdmin) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-lg text-red-500">Access Denied. You must be an admin to view this page.</div>
            </div>
        );
    }

    // Mobile card component
    const UserCard = ({ user }: { user: IUser }) => (
        <div className="user-card bg-card border border-border rounded-lg shadow-sm p-4 mb-3">
            <div className="flex flex-col space-y-3">
                <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                        <h3 className="user-name text-sm font-medium text-foreground truncate">
                            {user.name || 'No Name'}
                        </h3>
                        <p className="user-email text-xs text-muted-foreground truncate mt-1">
                            {user.email}
                        </p>
                    </div>
                    <div className="ml-2 flex-shrink-0">
                        <span className={`admin-badge inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${user.is_admin
                            ? 'admin bg-primary text-primary-foreground'
                            : 'user bg-muted text-muted-foreground'
                            }`}>
                            {user.is_admin ? 'Admin' : 'User'}
                        </span>
                    </div>
                </div>

                <div className="user-id text-xs text-muted-foreground">
                    ID: {user._id}
                </div>

                <div className="action-buttons flex flex-row sm:flex-row gap-2 pt-2">
                    <Button
                        onClick={() => handleImpersonate(user)}
                        fullWidth={false}
                        className="action-button flex items-center justify-center gap-2 border border-border bg-background hover:bg-accent text-xs h-8 px-3 flex-1"
                    >
                        <UserCheck className="h-3 w-3" />
                        Impersonate
                    </Button>
                    <Button
                        onClick={() => handleAddCredit(user)}
                        className="action-button flex items-center justify-center gap-2 border border-border bg-background hover:bg-accent text-xs h-8 px-3 flex-1"
                    >
                        <Coins className="h-3 w-3" />
                        Add Credit
                    </Button>
                    <Button
                        onClick={() => handleMakePro(user)}
                        className="action-button flex items-center justify-center gap-2 border border-border bg-background hover:bg-accent text-xs h-8 px-3 flex-1 text-amber-600"
                    >
                        <Crown className="h-3 w-3" />
                        Make Pro
                    </Button>
                </div>
            </div>
        </div>
    );

    // Mobile pagination component
    const MobilePagination = () => (
        <div className="mobile-pagination flex flex-col sm:flex-row justify-between items-center gap-4 mt-6">
            <div className="pagination-info text-sm text-muted-foreground">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalUsers)} of {totalUsers} users
            </div>

            <div className="pagination-controls flex items-center gap-2">
                <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    className="page-size-selector text-sm border border-border rounded px-2 py-1 bg-background text-foreground"
                >
                    <option value={10}>10 per page</option>
                    <option value={20}>20 per page</option>
                    <option value={50}>50 per page</option>
                </select>

                <div className="flex items-center gap-1">
                    <Button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="page-button h-8 w-8 p-0"
                        variant="outline"
                    >
                        ←
                    </Button>
                    <span className="text-sm px-2 text-foreground">
                        {currentPage} of {totalPages}
                    </span>
                    <Button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="page-button h-8 w-8 p-0"
                        variant="outline"
                    >
                        →
                    </Button>
                </div>
            </div>
        </div>
    );

    const ProjectCard = ({ project }: { project: any }) => (
        <div className="project-card bg-card border border-border rounded-lg shadow-sm p-4 mb-3">
            <div className="flex flex-col space-y-3">
                <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                        <h3 className="project-name text-sm font-medium text-foreground truncate">
                            {project.name || 'Unnamed Project'}
                        </h3>
                        <p className="project-email text-xs text-muted-foreground truncate mt-1">
                            {project.user_email || 'No email'}
                        </p>
                    </div>
                    <div className="ml-2 flex-shrink-0">
                        <span className="app-type-badge inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                            {project.app_type?.toUpperCase() || 'N/A'}
                        </span>
                    </div>
                </div>

                <div className="project-id text-xs text-muted-foreground">
                    ID: {project._id || project.id}
                </div>

                {project.created_at && (
                    <div className="project-date text-xs text-muted-foreground">
                        Created: {commonService.transformTimeStamp(project.created_at)}
                    </div>
                )}

                <div className="action-buttons flex flex-row sm:flex-row gap-2 pt-2">
                    <Button
                        onClick={() => handleImpersonateProject(project)}
                        className="action-button flex items-center justify-center gap-2 border border-border bg-background hover:bg-accent text-xs h-8 px-3 flex-1"
                    >
                        <UserCheck className="h-3 w-3" />
                        Impersonate
                    </Button>
                </div>
            </div>
        </div>
    );

    // Projects pagination component
    const ProjectsMobilePagination = () => (
        <div className="mobile-pagination flex flex-col sm:flex-row justify-between items-center gap-4 mt-6">
            <div className="pagination-info text-sm text-muted-foreground">
                Showing {((projectsCurrentPage - 1) * projectsPageSize) + 1} to {Math.min(projectsCurrentPage * projectsPageSize, totalProjects)} of {totalProjects} projects
            </div>

            <div className="pagination-controls flex items-center gap-2">
                <select
                    value={projectsPageSize}
                    onChange={(e) => handleProjectsPageSizeChange(Number(e.target.value))}
                    className="page-size-selector text-sm border border-border rounded px-2 py-1 bg-background text-foreground"
                >
                    <option value={10}>10 per page</option>
                    <option value={20}>20 per page</option>
                    <option value={50}>50 per page</option>
                </select>

                <div className="flex items-center gap-1">
                    <Button
                        onClick={() => handleProjectsPageChange(projectsCurrentPage - 1)}
                        disabled={projectsCurrentPage === 1}
                        className="page-button h-8 w-8 p-0"
                        variant="outline"
                    >
                        ←
                    </Button>
                    <span className="text-sm px-2 text-foreground">
                        {projectsCurrentPage} of {projectsTotalPages}
                    </span>
                    <Button
                        onClick={() => handleProjectsPageChange(projectsCurrentPage + 1)}
                        disabled={projectsCurrentPage === projectsTotalPages}
                        className="page-button h-8 w-8 p-0"
                        variant="outline"
                    >
                        →
                    </Button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="user-list-screen p-2 mx-auto min-h-screen bg-background">
            <div className={`mobile-header flex ${isMobile ? 'flex-col' : 'justify-between items-center'} mb-5 gap-2`}>
                <div className={`search-container flex items-center gap-1 ${isMobile ? 'w-full' : 'flex-1'}`}>
                    <h1 className={`text-2xl font-bold text-foreground ${isMobile ? 'w-full' : 'w-80'}`}>
                        Admin Dashboard
                    </h1>
                    {activeTab === 'users' && (
                        <Input
                            type="text"
                            placeholder="Search users by name, email, or role..."
                            value={searchQuery}
                            fullWidth={isMobile}
                            icon={<Search className="absolute left-3 top-2 text-muted-foreground h-4 w-4" />}
                            onChange={handleSearchChange}
                            className="search-input h-8 mb-0"
                        />
                    )}
                    {activeTab === 'projects' && (
                        <Input
                            type="text"
                            placeholder="Search projects by name, ID, or user email..."
                            value={projectsSearchQuery}
                            fullWidth={isMobile}
                            icon={<Search className="absolute left-3 top-2 text-muted-foreground h-4 w-4" />}
                            onChange={handleProjectsSearchChange}
                            className="search-input h-8 mb-0"
                        />
                    )}
                </div>
                <div className={`action-buttons flex items-center gap-3 ${isMobile ? 'w-full justify-between mt-2' : ''}`}>
                    {activeTab === 'users' && (
                        <Button
                            onClick={() => handleImpersonate(currentUser)}
                            className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm h-8 px-3"
                        >
                            <UserCheck className="h-4 w-4" />
                            {isMobile ? 'Self' : 'Proceed as Self'}
                        </Button>
                    )}
                    <div className={`user-info text-sm text-muted-foreground ${isMobile ? 'hidden sm:block' : ''}`}>
                        Logged in as: {currentUser?.name} ({currentUser?.email})
                    </div>
                </div>
            </div>

            {impersonationError && (
                <div className="error-message mb-4 p-4 bg-destructive/10 border border-destructive/20 text-destructive rounded">
                    {impersonationError}
                </div>
            )}

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="mb-4">
                    <TabsTrigger value="users" className="flex items-center gap-2">
                        <UserCheck className="h-4 w-4" />
                        Users
                    </TabsTrigger>
                    <TabsTrigger value="projects" className="flex items-center gap-2">
                        <FolderKanban className="h-4 w-4" />
                        Projects
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="users" className="mt-0">
                    {/* Desktop Table View for Users */}
                    {!isMobile && (
                        <TableWrapperComponent
                            dataExtractorKey="users"
                            id="user-list-table"
                            columns={columns}
                            url={APIConfig.GET_ALL_USERS.URL}
                            method={APIConfig.GET_ALL_USERS.METHOD}
                            isPaginated={true}
                            extraPayload={searchPayload}
                            moduleName="user-list"
                            noDataText="No users found."
                            errorText="Failed to load users."
                            rowKey={(record: IUser) => String(record._id)}
                            className="rounded-lg shadow overflow-hidden"
                        />
                    )}

                    {/* Mobile Card View for Users */}
                    {isMobile && (
                        <div className="mobile-content">
                            {isLoading ? (
                                <div className="loading-state flex items-center justify-center py-12">
                                    <Loader2 className="loading-icon h-8 w-8 animate-spin text-muted-foreground" />
                                    <span className="loading-text ml-2 text-muted-foreground">Loading users...</span>
                                </div>
                            ) : isError ? (
                                <div className="error-state flex items-center justify-center py-12">
                                    <AlertCircle className="error-icon h-8 w-8 text-destructive" />
                                    <span className="error-text ml-2 text-destructive">Failed to load users</span>
                                </div>
                            ) : users.length === 0 ? (
                                <div className="empty-state text-center py-12">
                                    <p className="empty-text text-muted-foreground">No users found.</p>
                                </div>
                            ) : (
                                <>
                                    <div className="users-list-container max-h-[calc(100vh-300px)] overflow-y-auto pr-2 space-y-3">
                                        {users.map((user) => (
                                            <UserCard key={user._id} user={user} />
                                        ))}
                                    </div>
                                    <MobilePagination />
                                </>
                            )}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="projects" className="mt-0">
                    {/* Desktop Table View for Projects */}
                    {!isMobile && (
                        <TableWrapperComponent
                            dataExtractorKey="projects"
                            id="projects-list-table"
                            columns={projectsColumns}
                            url={APIConfig.GET_ALL_PROJECTS.URL}
                            method={APIConfig.GET_ALL_PROJECTS.METHOD}
                            isPaginated={true}
                            extraPayload={projectsSearchPayload}
                            moduleName="projects-list"
                            noDataText="No projects found."
                            errorText="Failed to load projects."
                            rowKey={(record: any) => String(record._id)}
                            className="rounded-lg shadow overflow-hidden"
                        />
                    )}

                    {/* Mobile Card View for Projects */}
                    {isMobile && (
                        <div className="mobile-content">
                            {isProjectsLoading ? (
                                <div className="loading-state flex items-center justify-center py-12">
                                    <Loader2 className="loading-icon h-8 w-8 animate-spin text-muted-foreground" />
                                    <span className="loading-text ml-2 text-muted-foreground">Loading projects...</span>
                                </div>
                            ) : isProjectsError ? (
                                <div className="error-state flex items-center justify-center py-12">
                                    <AlertCircle className="error-icon h-8 w-8 text-destructive" />
                                    <span className="error-text ml-2 text-destructive">Failed to load projects</span>
                                </div>
                            ) : projects.length === 0 ? (
                                <div className="empty-state text-center py-12">
                                    <p className="empty-text text-muted-foreground">No projects found.</p>
                                </div>
                            ) : (
                                <>
                                    <div className="projects-list-container max-h-[calc(100vh-300px)] overflow-y-auto pr-2 space-y-3">
                                        {projects.map((project) => (
                                            <ProjectCard key={project._id} project={project} />
                                        ))}
                                    </div>
                                    <ProjectsMobilePagination />
                                </>
                            )}
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            <AddCreditDialogComponent
                isOpen={isAddCreditDialogOpen}
                onClose={handleCloseAddCreditDialog}
                user={selectedUser}
            />
        </div>
    );
};

export default UserListScreen;